"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveDatabaseRootDocId = resolveDatabaseRootDocId;
exports.toYCompatibleValue = toYCompatibleValue;
exports.placeBlockChild = placeBlockChild;
exports.buildPageBlock = buildPageBlock;
exports.buildColumnArray = buildColumnArray;
exports.buildDatabaseRootBlock = buildDatabaseRootBlock;
exports.databaseProvisionRootJs = databaseProvisionRootJs;
/**
 * The database root document as the platform creates it: its page block
 * and its `affine:database` storage block with schema, views and identity.
 * Server and StreamKit build new databases with these builders, so both
 * write the same document.
 */
const Y = __importStar(require("yjs"));
const database_deleted_columns_1 = require("./database-deleted-columns");
const database_document_identity_1 = require("./database-document-identity");
const index_1 = require("./index");
const page_document_maps_1 = require("./page-document-maps");
const page_structure_writer_1 = require("./page-structure-writer");
/** The root document of a database: the bound one or the server default. */
function resolveDatabaseRootDocId(databaseId, identityBinding) {
    return (identityBinding.databaseRootDocId ??
        (0, database_document_identity_1.createServerDatabaseRootDocId)({
            workspaceId: identityBinding.workspaceId,
            databaseId,
        }));
}
/** Plain objects and arrays as Yjs maps and arrays; other values as they are. */
function toYCompatibleValue(value) {
    if (Array.isArray(value)) {
        const array = new Y.Array();
        array.push(value.map(item => toYCompatibleValue(item)));
        return array;
    }
    if (value &&
        typeof value === 'object' &&
        Object.getPrototypeOf(value) === Object.prototype) {
        const map = new Y.Map();
        for (const [nestedKey, nestedValue] of Object.entries(value)) {
            map.set(nestedKey, toYCompatibleValue(nestedValue));
        }
        return map;
    }
    return value;
}
/**
 * Places a block whose fields the page document already holds: under
 * `parentId`, at `index` or at the end. A block placed under that parent
 * already stays where it is.
 */
function placeBlockChild(doc, parentId, childId, index) {
    const writer = new page_structure_writer_1.PageStructureWriter((0, page_structure_writer_1.pageDocumentStructure)(doc));
    const placed = writer.entries().get(childId);
    if (placed) {
        if (placed.p === parentId)
            return;
        throw new page_structure_writer_1.PageStructureWriteError(`Block ${childId} lies under ${placed.p}, not ${parentId}`);
    }
    writer.placeBlock({
        id: childId,
        parentId,
        index,
        partId: page_document_maps_1.PAGE_DOCUMENT_PART_ID,
    });
}
function buildTitleText(title) {
    const titleText = new Y.Text();
    titleText.insert(0, title);
    return titleText;
}
/** Build an `affine:page` block and place it as the root of the page tree. */
function buildPageBlock(doc, blocks, pageId, title) {
    const block = new Y.Map();
    block.set('sys:id', pageId);
    block.set('sys:flavour', 'affine:page');
    block.set('sys:version', 2);
    block.set('prop:title', buildTitleText(title));
    blocks.set(pageId, block);
    placeBlockChild(doc, null, pageId);
}
function buildCanvasPresentation(mode, viewId) {
    const state = new Y.Map();
    state.set('mode', mode);
    if (mode === 'live') {
        state.set('viewId', viewId);
        state.set('viewport', toYCompatibleValue({
            scrollLeft: 0,
            scrollTop: 0,
        }));
    }
    state.set('updatedAt', Date.now());
    return state;
}
function buildDatabaseColumnMap(column) {
    const colMap = new Y.Map();
    colMap.set('id', column.id);
    colMap.set('name', column.name);
    colMap.set('type', column.type);
    const dataMap = new Y.Map();
    if (column.data) {
        for (const [k, v] of Object.entries(column.data)) {
            dataMap.set(k, toYCompatibleValue(v));
        }
    }
    colMap.set('data', dataMap);
    return colMap;
}
/** The `prop:columns` of a database block for `columns`, as provisioning builds it. */
function buildColumnArray(columns) {
    const colsArray = new Y.Array();
    for (const col of columns) {
        colsArray.push([buildDatabaseColumnMap(col)]);
    }
    return colsArray;
}
function buildDatabaseTableViewColumnMap(column) {
    const vc = new Y.Map();
    vc.set('id', column.id);
    vc.set('width', column.width ?? 150);
    vc.set('hide', column.hide ?? false);
    return vc;
}
function buildDefaultTableViews(columns, viewSpec) {
    const viewsArray = new Y.Array();
    const view = new Y.Map();
    view.set('id', viewSpec?.id ?? crypto.randomUUID());
    view.set('name', 'Table View');
    view.set('mode', 'table');
    const viewCols = new Y.Array();
    const columnSpecs = viewSpec?.columns ??
        columns.map(col => ({
            id: col.id,
            width: col.type === 'title' ? 260 : 150,
            hide: false,
        }));
    for (const col of columnSpecs) {
        viewCols.push([buildDatabaseTableViewColumnMap(col)]);
    }
    view.set('columns', viewCols);
    view.set('filter', toYCompatibleValue({
        type: 'group',
        op: 'and',
        conditions: [],
    }));
    view.set('header', toYCompatibleValue({
        titleColumn: 'title',
        iconColumn: 'type',
    }));
    viewsArray.push([view]);
    return viewsArray;
}
/**
 * Build the DatabaseRootDoc storage block. It owns schema, views and the
 * row-shard manifest. It has no PageDoc placement or parent knowledge.
 */
function buildDatabaseRootBlock(blocks, databaseId, title, columns, identityBinding, viewSpec, canvasPresentationMode = 'summary') {
    const block = new Y.Map();
    block.set('sys:id', databaseId);
    block.set('sys:flavour', 'affine:database');
    block.set('sys:version', 3);
    (0, index_1.writeSerializedShardedDatabaseIdentity)(block, {
        databaseId,
        workspaceId: identityBinding.workspaceId,
        databaseRootDocId: resolveDatabaseRootDocId(databaseId, identityBinding),
    });
    block.set('prop:title', buildTitleText(title));
    block.set('prop:columns', buildColumnArray(columns));
    // Created with the block: concurrent first deletes merge key by key.
    block.set(database_deleted_columns_1.DATABASE_DELETED_COLUMNS_PROP, new Y.Map());
    const viewId = viewSpec?.id ?? crypto.randomUUID();
    block.set('prop:canvasPresentation', buildCanvasPresentation(canvasPresentationMode, viewId));
    block.set('prop:views', buildDefaultTableViews(columns, {
        ...viewSpec,
        id: viewId,
    }));
    blocks.set(databaseId, block);
}
/** Builds a new database root document: page block, database block, views. */
function databaseProvisionRootJs(input) {
    const doc = new Y.Doc({ guid: input.databaseRootDocId });
    try {
        const blocks = doc.getMap('blocks');
        doc.transact(() => {
            buildPageBlock(doc, blocks, input.rootPageId, '');
            buildDatabaseRootBlock(blocks, input.databaseId, input.title, input.columns, {
                workspaceId: input.workspaceId,
                databaseRootDocId: input.databaseRootDocId,
            }, { id: input.viewId, columns: input.viewColumns }, input.canvasPresentationMode);
            placeBlockChild(doc, input.rootPageId, input.databaseId);
            const block = blocks.get(input.databaseId);
            for (const [key, value] of Object.entries(input.properties ?? {})) {
                if (!key.startsWith('prop:'))
                    throw new Error(`Database root property must start with prop: (${key})`);
                block.set(key, value);
            }
            const views = block.get('prop:views');
            for (const view of input.additionalViews ?? [])
                views.push([toYCompatibleValue(view)]);
        });
        return {
            data: Y.encodeStateAsUpdate(doc),
            block: blocks.get(input.databaseId).toJSON(),
        };
    }
    finally {
        doc.destroy();
    }
}
